import React, { useState, useEffect, useRef } from 'react';
import Config from '../../config';
import {
    Link as MuiLink,
    Grid,
    Card,
    Typography,
    FormControlLabel,
    Button,
    Dialog,
    Toolbar,
    MenuItem,
    DialogContent,
    DialogActions,
    IconButton,
    CardContent,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TableHead,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import Switch from '../../components/switch';
import Select from '../../components/select_field';
import AutoComplete from '../../components/fast_suggest_field';
import TextField from '../../components/text_field';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import { getIn } from 'final-form';
import InfoIcon from '@mui/icons-material/Info';
import DragIndicator from '@mui/icons-material/DragIndicator';
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";
import arrayMutators from 'final-form-arrays';
import createDecorator from 'final-form-calculate'
import { FieldArray } from 'react-final-form-arrays';
import Helper from '../../helpers';
import Auth from '../../auth';
import { EventTypes, Categories } from './event_types';
import type { EventTypeInfo, CategoryValue } from './event_types';
import ErrorsCategoryIcon from '../../images/errors.svg';
import WarningsCategoryIcon from '../../images/warnings.svg';
import InfoCategoryIcon from '../../images/info.svg';
import UserIcon from '../../images/user.svg';
import ScheduleIcon from '../../images/schedule.svg';
import SearchBox from '../../components/searchbox';
import { Link } from 'react-router-dom';
import EventModal from '../messages/event_modal';
import { Header } from '../../theme';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (established pattern, see
// reports.tsx/scorecards.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;

const StyledCardContent = styled(CardContent)({
    display: 'flex',
    flexDirection: 'column',
    '& label': {
        marginBottom: '0.5rem'
    }
});

const reportEventTypes: Record<string, number[]> = {
    'ClientActivityReport': [23],
    'DocumentAgingReport': [47],
    'FailedDocumentReport': [30],
    'FuncAckReport': [26],
    'InventoryUnmatchedReport': [41],
    'InventoryUpdateReport': [40, 45],
    'MonthlyDocumentReport': [19, 24],
    'MonthlyOrderReport': [19, 25],
    'NewDocumentActivityReport': [19],
    'OpenDocumentReport': [27],
    'ScoreCardReport': [58],
    'ShippedOrderAgingReport': [47],
    'SubmittedDocumentReport': [27],
    'UnshippedOrderReport': [27]
}

type DraggableProps = {
    children?: React.ReactNode;
    style?: React.CSSProperties;
};

// react-sortable-hoc's SortableHandle/SortableElement/SortableContainer are
// generic over the wrapped component's props (P), and P is declared only in
// a union of function-type parameter positions (WrappedComponent<P>) --
// TS cannot reliably infer P from an annotated arrow-function argument alone
// in that position, so the type argument is supplied explicitly at each call
// below instead of relying on inference.
const Draggable = SortableHandle<DraggableProps>(({children, style}) => (
  <div style={{ cursor: "move", ...style }}>{children}</div>
));


const sortEnd = (move: (oldIndex: number, newIndex: number) => void) => ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
  move(oldIndex, newIndex);
};

// `node` matches react-sortable-hoc's SortStart.node (typed as the DOM-lib
// `Element`, not `HTMLElement`); narrow to HTMLElement below where style/
// offsetWidth are actually used.
const sortStart = ({ node, helper }: { node: Element; helper: HTMLElement }) => {
      node.childNodes.forEach((td, index) => {
        (helper.childNodes[index] as HTMLElement).style.width = `${(td as HTMLElement).offsetWidth}px`;
      });
    }

const getTableContainer = () => document.getElementById("fieldTable");

const EventTypeOptions = [<MenuItem key="0" value="">Any event</MenuItem>].concat(EventTypes.Info.concat(EventTypes.Error).concat(EventTypes.Warning)
    .sort((a,b) => a.description > b.description ? 1 : -1)
    .map(r => <MenuItem key={r.id} value={r.id}>{r.description}</MenuItem>));

const getOperators = (field: string) => {
    let operators = [{label: 'equals', value: 'equals'}, {label: 'does not equal', value: 'not equals'}];
    if(field !== "partner"){
        operators.push({label: 'contains', value: 'contains'});
        operators.push({label: 'does not contain', value: 'not contains'});
    }
    return operators.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>);
}

type IfSetProps = {
    field: string;
    children: React.ReactNode | ((props: { value: any }) => React.ReactNode);
};

const IfSet = ({ field, children }: IfSetProps) => (
  <Field name={field} subscription={{ value: true }}>
    {({ input: { value } }: any) => (!(!value) ? typeof children === 'function' ? (children as (props: { value: any }) => React.ReactNode)({value}) : children : null)}
  </Field>
)

type SortableItemProps = {
    name: string;
    fields: any;
    value: number;
    partnerOptions: any[];
};

const SortableItem = SortableElement<SortableItemProps>(({ name, fields, value, partnerOptions }) => (
    <TableRow style={{backgroundColor: "white"}}>
    <TableCell padding="checkbox">
        <Draggable style={{display: 'flex', alignItems: 'center', width: '2em', fontSize: '16px'}}><DragIndicator/> {value + 1}</Draggable>
    </TableCell>
    <TableCell>
      <Field component={Select} formControlProps={{style: {width: '100%'}}} name={`${name}.Action`}>
        <MenuItem value="allow">Allow</MenuItem>
        <MenuItem value="block">Block</MenuItem>
      </Field>
    </TableCell>
    <TableCell>
      <Field component={Select} displayEmpty={true} formControlProps={{style: {width: '100%'}}} style={{textAlign: 'left'}} name={`${name}.EventType`}>
      {EventTypeOptions}
      </Field>
    </TableCell>
    <TableCell>
      <Field component={Select} format={(v: any) => !v ? "" : v} parse={(v: any) => !v ? null : v} displayEmpty={true} formControlProps={{style: {width: '100%'}}} style={{textAlign: 'left'}} name={`${name}.Condition`}>
        <MenuItem value="">Always</MenuItem>
        <MenuItem value="if">If</MenuItem>
      </Field>
    </TableCell>
    <TableCell>
      <IfSet field={`${name}.Condition`}>
          <Field component={Select} formControlProps={{style: {width: '100%'}}} validate={(v: any) => (!v)} name={`${name}.Field`}>
            <MenuItem value="partner">Partner</MenuItem>
            <MenuItem value="subject">Subject</MenuItem>
            <MenuItem value="body">Body</MenuItem>
          </Field>
      </IfSet>
    </TableCell>
    <TableCell>
    <IfSet field={`${name}.Condition`}>
    <IfSet field={`${name}.Field`}>
          {({value}: {value: any}) => <Field component={Select} formControlProps={{style: {width: '100%'}}} validate={(v: any) => (!v)} name={`${name}.Operator`}>{getOperators(value)}</Field>}
    </IfSet>
    </IfSet>
    </TableCell>
      <TableCell>
        <IfSet field={`${name}.Condition`}>
            <IfSet field={`${name}.Field`}>
              {({value}: {value: any}) => {
                  if(value === 'partner'){
                      return <Field style={{textAlign: 'left', width: '100%'}} validate={(v: any) => (!v)} component={AutoComplete} options={partnerOptions} controlled name={`${name}.Value`}/>
                  }
                  return <Field style={{textAlign: 'left', width: '100%'}} validate={(v: any) => (!v)} component={TextField} name={`${name}.Value`}/>;
              }}
            </IfSet>
        </IfSet>
      </TableCell>
      <TableCell padding="none" style={{alignSelf: 'center'}}><Button variant='outlined' onClick={() => fields.remove(value)}>Remove</Button></TableCell>
    </TableRow>
));

type SortableListProps = {
    items: any;
    partnerOptions: any[];
    values: any;
};

const SortableList = SortableContainer<SortableListProps>(({ items, partnerOptions, values }) => {
  return (
    <TableBody id="fieldTable">
      {items.map((name: string, index: number) => (
        <SortableItem
          key={getIn(values, name + ".key")}
          index={index}
          value={index}
          name={name}
          fields={items}
          partnerOptions={partnerOptions}
        />
      ))}
    </TableBody>
  );
});

const ruleDecorator = createDecorator(
    {
        field: /\.Field/,
        updates: (value: any, name: string, allValues: any, prevValues: any) => {
            let valueField = name.replace(".Field", ".Value");
            let operatorField = name.replace(".Field", ".Operator");
            let currentField = getIn(allValues, name);
            let prevField = getIn(prevValues, name);
            let currentOperator = getIn(allValues, operatorField);
            let ret: any = {};
            if(currentField === 'partner' && prevField && prevField !== 'partner'){
                ret[valueField] = null;
                if(currentOperator !== 'equals' && currentOperator !== 'not equals'){
                    ret[operatorField] = 'equals';
                }
            } else if (prevField === 'partner' && currentField !== 'partner'){
                ret[valueField] = null;
            }
            return ret;
        }
    },
    {
        field: /\.Condition/,
        updates: (value: any, name: string, allValues: any, prevValues: any) => {
            let valueField = name.replace(".Condition", ".Value");
            let operatorField = name.replace(".Condition", ".Operator");
            let fieldField = name.replace(".Condition", ".Field");
            let condition = getIn(allValues, name);
            let ret: any = {};
            if(!condition){
                ret[valueField] = null;
                ret[operatorField] = null;
                ret[fieldField] = null;
            }
            return ret;
        }
    }
)

type FilterButonProps = {
    children?: React.ReactNode;
    onClick: (value: any) => void;
    category: any;
    value: any;
};

const FilterButon = ({ children, onClick, category, value }: FilterButonProps) => (
    <Button variant='outlined' color={category === value ? 'primary' : undefined} onClick={() => onClick(value)}>{children}</Button>
);

type EmailRule = {
    key?: string | number;
    Id?: string | number;
    Priority?: number;
    Action?: string;
    EventType?: any;
    Condition?: string;
    Field?: string;
    Operator?: string;
    Value?: any;
};

type NotificationsProps = {
    setTitle?: (title: string) => void;
    coid?: number | string;
    companyName?: string;
    isBillable?: boolean;
};

const Notifications = (props: NotificationsProps) => {
    const { setTitle, coid, companyName, isBillable } = props;
    const [notifications, setNotifications] = useState<Record<string, boolean>>({});
    const [confirmUnsubscribe, setConfirmUnsubscribe] = useState(false);
    const [reportDefinitions, setReportDefinitions] = useState<any[]>([]);
    const [showEventDetails, setShowEventDetails] = useState(false);
    const [showError, setShowError] = useState(false);
    const [category, setCategoryState] = useState<CategoryValue>(Categories.All);
    const [term, setTerm] = useState('');
    const [showScheduledDetails, setShowScheduledDetails] = useState(false);
    const [rules, setRules] = useState<{ Rules: EmailRule[] } | undefined>(undefined);
    const [partnerOptions, setPartnerOptions] = useState<{ value: string; label: string }[] | undefined>(undefined);
    const [event, setEvent] = useState<EventTypeInfo | undefined>(undefined);
    const [eventReports, setEventReports] = useState<string[] | undefined>(undefined);
    const [eventId, setEventId] = useState<any>(undefined);
    const [disableUnsubscribe, setDisableUnsubscribe] = useState<boolean | undefined>(undefined);
    const [error, setError] = useState<string | undefined>(undefined);
    const [previewEventId, setPreviewEventId] = useState<any>(undefined);
    const cancelSource = useRef(axios.CancelToken.source());

    const loadNotifications = async (loadDefinitions?: boolean) => {
        let defCall = async () => (await axios.get(Config.api + "/odata/Company/ReportDefinitions?$select=Id,Description,ReportName&$orderby=Description")).data.value;
        let resCall = async () => (await axios.get(Config.api + `/odata/Company/ActivityEventSubscription/?&$select=EventTypeId&$filter=(CrmUserId eq '${Auth.getUserId()}') and IsSubscribed`)).data.value;
        let rulesCall = async () => (await axios.get(Config.api + `/odata/Company/EmailRules`)).data.value || [];
        let partnersCall = async () => (await axios.get(Config.api + '/api/v1/Partners')).data.Body.Partners;
        let partners: any[] = [];
        let reportDefinitions: any[] = [];
        let res: any[] = [];
        let rules: EmailRule[] = [];
        if(loadDefinitions){
            [reportDefinitions, res, rules, partners] = await Promise.all([defCall(), resCall(), rulesCall(), partnersCall()]);
        }else{
            [res, rules] = await Promise.all([resCall(), rulesCall()]);
        }
        let notifications: Record<string, boolean> = {};
        EventTypes.Info.every(r => {notifications["_" + r.id] = false; return true;});
        EventTypes.Error.every(r => {notifications["_" + r.id] = false; return true;});
        EventTypes.Warning.every(r => {notifications["_" + r.id] = false; return true;});
        if(res){
            res.every((r: any) => {notifications["_" + r.EventTypeId] = true; return true;});
        }
        rules.forEach(r => r.key = r.Id);
        if(loadDefinitions){
            let partnerOptions = partners.map((r: any) => {return {value: r.Id.toString(), label: r.CompanyName}});
            partnerOptions.push({value: (coid as any).toString(), label: companyName});
            partnerOptions = partnerOptions.sort((a, b) => a.label.localeCompare(b.label));
            setNotifications(notifications);
            setRules({Rules: rules});
            setReportDefinitions(reportDefinitions);
            setPartnerOptions(partnerOptions);
        }else{
            setNotifications(notifications);
            setRules({Rules: rules});
        }
    };
    const onEventClick = async (event: EventTypeInfo) => {
        cancelSource.current.cancel("Event clicked.");
        cancelSource.current = axios.CancelToken.source();
        let reportNames = Object.keys(reportEventTypes).filter(k => reportEventTypes[k].indexOf(event.id) >= 0);
        let filteredReports = (reportDefinitions || []).filter((r: any) => reportNames.indexOf(r.ReportName) >= 0);
        let eventIdReq = axios.get(Config.api + `/odata/Company/VwActivityEvents?$filter=EventTypeId eq ${event.id}&$orderby=Id desc&$top=1`, {
            cancelToken: cancelSource.current.token
        });
        setShowEventDetails(true);
        setEvent(event);
        setEventReports(filteredReports.map((r: any) => r.Description));
        setEventId(null);
        let eventId = ((await eventIdReq).data.value[0] || {}).Id;
        setEventId(eventId);
    };
    useEffect(() => {
        if (setTitle) {
            setTitle('Notifications');
        }
        loadNotifications(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const onSubmit = async (e: Record<string, boolean>) => {
        var evts = Object.keys(e).filter(key => e[key]).map(key => key.substring(1));
        await axios.get(Config.api + `/odata/Company/Functions.SubscribeToEvents?userid=${Auth.getUserId()}&eventIds=${evts.length > 0 ? evts.join(',') : 'null'}&email=${Auth.getEmail()}`);
        loadNotifications();
    };
    const renderEvents = (events: EventTypeInfo[]) => {
        let filtered = events.filter(r => (category === Categories.All || category === r.category) && (!term || r.description.toLowerCase().indexOf(term.toLowerCase()) > -1));
        let notScheduled = filtered.filter(r => !r.scheduled);
        let scheduled = filtered.filter(r => r.scheduled);
        if(filtered.length === 0){
            return <Typography style={{color: "#9E9E9E"}}>No notifications matching your filters.</Typography>
        }
        return <>
            {notScheduled.map(item => {
            return (<div key={"_" + item.id} style={{textAlign: 'left'}}>
                <FormControlLabel style={{height: '1.5em'}} control={<Field type="checkbox" component={Switch} name={"_" + item.id}/>} label={item.description}/>
                <IconButton style={{position: 'absolute', marginLeft: '-12px', marginTop: '-2px'}} color='primary' onClick={() => onEventClick(item)} size="small"><InfoIcon fontSize="inherit" /></IconButton>
                </div>)})}
            {scheduled.length > 0 && <>
                <Typography style={{color: "#9E9E9E", fontWeight: 'bold', marginTop: '1em', marginBottom: '0.5em'}}>Scheduled Reports <IconButton color='primary' onClick={() => setShowScheduledDetails(true)} size="small"><InfoIcon fontSize="inherit" /></IconButton></Typography>
                {scheduled.map(item => {
                return (<div key={"_" + item.id} style={{textAlign: 'left'}}>
                    <FormControlLabel style={{height: '1.5em'}} control={<Field type="checkbox" component={Switch} name={"_" + item.id}/>} label={item.description}/>
                    <IconButton style={{position: 'absolute', marginLeft: '-12px', marginTop: '-2px'}} color='primary' onClick={() => onEventClick(item)} size="small"><InfoIcon fontSize="inherit" /></IconButton>
                    </div>)})}
            </>}
        </>;
    };
    const unsubscribeAll = async () => {
        setDisableUnsubscribe(true);
        await axios.get(Config.api + '/odata/Company/Functions.UnsubscribeAll');
        setConfirmUnsubscribe(false);
        setDisableUnsubscribe(false);
        loadNotifications();
    };
    const onRuleSubmit = async (e: { Rules: EmailRule[] }) => {
        let rules = e.Rules.map((r, i) => ({...r, Priority: i + 1}));
        try{
            await axios.post(Config.api + '/odata/Company/Functions.UpdateEmailRules', rules);
            await loadNotifications();
        }catch(ex: any){
            let error = 'An unexpected error occurred.';
            if(ex.response && ex.response.data){
                error = new Helper().getApiErrors(ex.response.data).join("\n")
            }
            setShowError(true);
            setError(error);
        }
    };
    const setCategory = (category: CategoryValue) => {
        setCategoryState(category);
    };
    const updateTerm = (e: any) => {
        setTerm(e.currentTarget.value);
    };
    return (<Grid container spacing={2} style={{padding: "24px 14px 24px 14px"}}>
        <Grid size={12} style={{textAlign: 'left', display: 'flex'}}>
            <Header>Notifications</Header>
            <div style={{flex: 1}}></div>
            {isBillable && <Button component={Link} variant='outlined' to='/settings/monitoring'>Scheduled Reports</Button>}
        </Grid>
        <Grid size={12}>
            <Card>
                <CardContent style={{display: 'flex', gap: '1em', textAlign: 'left', padding: '10px'}}>
                    <FilterButon category={category} onClick={setCategory} value={Categories.All}>All</FilterButon>
                    <FilterButon category={category} onClick={setCategory} value={Categories.Orders}>Order Lifecycle</FilterButon>
                    <FilterButon category={category} onClick={setCategory} value={Categories.Inventory}>Inventory/Product</FilterButon>
                    <FilterButon category={category} onClick={setCategory} value={Categories.Partners}>Partners/Onboarding</FilterButon>
                    <FilterButon category={category} onClick={setCategory} value={Categories.Connectivity}>Connectivity</FilterButon>
                    <div style={{flex: 1}}></div>
                    <SearchBox handleKeyUp={updateTerm} handleChange={updateTerm} placeholder={'Search'}/>
                </CardContent>
            </Card>
        </Grid>
        <Grid size={12} style={{display: 'flex'}}>
            <Card style={{flex: 1}}>
                <StyledCardContent>
                    <Form onSubmit={onSubmit} initialValues={notifications} render={({ handleSubmit, pristine, invalid }: any) => {
                          return (
                              <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', textAlign: 'left'}}>
                              <Grid container>
                                <Grid size={{ md: 4 }} style={{display: 'flex', flexDirection: 'column'}}>
                                    <Typography variant="h6" component="h2" style={{marginBottom: '1em', display: 'flex', alignItems: 'center', gap: '10px'}}>Info <img alt='Info' src={InfoCategoryIcon}/></Typography>
                                    {renderEvents(EventTypes.Info)}
                                </Grid>
                                <Grid size={{ md: 4 }} style={{display: 'flex', flexDirection: 'column'}}>
                                    <Typography variant="h6" component="h2" style={{marginBottom: '1em', display: 'flex', alignItems: 'center', gap: '10px'}}>Errors <img alt='Errors' src={ErrorsCategoryIcon}/></Typography>
                                    {renderEvents(EventTypes.Error)}
                                </Grid>
                                <Grid size={{ md: 4 }} style={{display: 'flex', flexDirection: 'column'}}>
                                    <Typography variant="h6" component="h2" style={{marginBottom: '1em', display: 'flex', alignItems: 'center', gap: '10px'}}>Warnings <img alt='Warnings' src={WarningsCategoryIcon}/></Typography>
                                    {renderEvents(EventTypes.Warning)}
                                </Grid>
                              </Grid>
                              <div style={{width: '100%', textAlign: 'right', marginTop: '1em'}}>
                                  <Button variant='outlined' style={{marginRight: '1em'}} onClick={() => {setConfirmUnsubscribe(true); setDisableUnsubscribe(false);}}>Unsubscribe from all notifications</Button>
                                  <Button type='submit' disabled={pristine} variant='contained' color='primary'>Save</Button>
                              </div>
                              </form>
                          );}}/>
                </StyledCardContent>
            </Card>
        </Grid>
        <Grid size={12}>
            <Card>
                <CardContent style={{textAlign: 'left'}}>
                <Typography variant="h6" component="h2">Filters</Typography>
                <Typography style={{textAlign: 'left'}}>You may configure additional email filtering rules here. Rules will be processed in order from top to bottom. Please be aware that certain types of events such as new order notifications are summarized and cannot be filtered by partner.</Typography>
                <Form onSubmit={onRuleSubmit} initialValues={rules} mutators={{...arrayMutators}} decorators={[ruleDecorator]} render={({ handleSubmit, pristine, invalid, submitting, values }: any) => {
                      return (
                          <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                <FieldArray name="Rules">
                {({ fields }: any) => (
                    <>
                    <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" style={{width: '3em'}}>Order</TableCell>
                            <TableCell style={{width: '5em'}}>Action</TableCell>
                            <TableCell style={{width: '30em'}}>Notification Type</TableCell>
                            <TableCell style={{width: '8em'}}>Condition</TableCell>
                            <TableCell style={{width: '10em'}}>Field</TableCell>
                            <TableCell style={{width: '10em'}}>Operator</TableCell>
                            <TableCell style={{minWidth: '20em'}}>Value</TableCell>
                            <TableCell padding="none" style={{width: '8em'}}></TableCell>
                        </TableRow>
                    </TableHead>
                    <SortableList
                      helperClass={'tableDragElement'}
                      helperContainer={getTableContainer}
                      useDragHandle={true}
                      items={fields}
                      values={values}
                      onSortEnd={sortEnd(fields.move)}
                      onSortStart={sortStart}
                      partnerOptions={partnerOptions}
                    />
                    </Table>
                    <div style={{marginTop: '0.5em', textAlign: 'right'}}>
                        <Button variant='outlined' onClick={() => fields.push({ Action: 'block', Condition: 'if', Field: 'partner', Operator: 'equals', key: Math.random().toString(16).slice(2) })} disabled={fields.length >= 100}>Add filter</Button>
                        <Button variant='contained' color='primary' style={{marginLeft: '1em'}} onClick={handleSubmit} disabled={pristine || invalid || submitting}>Save</Button>
                    </div>
                    </>
                  )}
                </FieldArray>
                </form>)}}/>
                </CardContent>
            </Card>
        </Grid>
        <Dialog open={confirmUnsubscribe} onClose={() => {setConfirmUnsubscribe(false)}}>
            <Toolbar className='lbtoolbar'>Confirm</Toolbar>
            <DialogContent><Typography>{'Are you sure you want to unsubscribe from all Logicbroker notifications?'}</Typography></DialogContent>
            <DialogActions>
                <Button variant='text' disabled={disableUnsubscribe} onClick={() => setConfirmUnsubscribe(false)} color="primary">No</Button>
                <Button variant='text' disabled={disableUnsubscribe} onClick={unsubscribeAll} color="primary">Yes</Button>
            </DialogActions>
        </Dialog>
        <Dialog open={showError} onClose={() => {setShowError(false)}}>
            <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
            <DialogContent><Typography>{error}</Typography></DialogContent>
        </Dialog>
        <Dialog maxWidth='sm' fullWidth open={showScheduledDetails} onClose={() => {setShowScheduledDetails(false)}}>
            <Toolbar className='lbtoolbar'>Scheduled Reports</Toolbar>
            <DialogContent>
                <Typography>Scheduled Reports are automated, recurring reports that provide key data insights to monitor operations, track KPIs, and maintain visibility across trading partner activities.</Typography>
                <Typography style={{color: '#9E9E9E', marginTop: '1em'}}><img src={UserIcon} alt='Users' style={{marginRight: '10px'}}/><span style={{fontWeight: 'bold'}}>Available to customers only</span> contact <MuiLink href='mailto:sales@logicbroker.com'>sales@logicbroker.com</MuiLink> for more info</Typography>
            </DialogContent>
        </Dialog>
        <Dialog maxWidth='sm' fullWidth open={showEventDetails} onClose={() => {setShowEventDetails(false)}}>
            <Toolbar className='lbtoolbar'>{(event || ({} as Partial<EventTypeInfo>)).description}</Toolbar>
            <DialogContent>
                <Typography>{(event || ({} as Partial<EventTypeInfo>)).details}</Typography>
                <Typography style={{color: '#9E9E9E', marginTop: '1em'}}><img style={{marginRight: '10px'}} src={ScheduleIcon} alt='Schedule'/>{(event || ({} as Partial<EventTypeInfo>)).schedule}</Typography>
                {eventReports && eventReports.length > 0 && <><Typography style={{marginTop: '1em'}}>To receive this notification, enable the report(s) through the Scheduled Reports page:</Typography>
                <ul style={{margin: 0}}>
                    {eventReports.map((r, i) => <li key={i}><Typography>{r}</Typography></li>)}
                </ul></>}
            </DialogContent>
            <DialogActions style={{display: 'flex', justifyContent: 'right'}}>
                {eventReports && eventReports.length > 0 && isBillable && <Button component={Link} variant='text' to='/settings/monitoring'>Scheduled Reports</Button>}
                <Button disabled={!eventId} variant='text' onClick={async () => {setPreviewEventId(null); await Promise.resolve(); setPreviewEventId(eventId);}}>Preview</Button>
            </DialogActions>
        </Dialog>
        <EventModal eventId={previewEventId} viewed={true}/>
        </Grid>);
};

export default Notifications;
